import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useNotification } from "../components/Notifications";
import { mapApiErrorToNotification } from "../utils/notificationHelpers";
import { apiFetch } from "../api/client";

export default function VerifyEmail() {
  const [form, setForm] = useState({ email: "", otp: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const notify = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    try {
      const qs = new URLSearchParams(location.search);
      const email = qs.get("email");
      if (email) setForm((f) => ({ ...f, email: decodeURIComponent(email) }));
    } catch (err) {
      // ignore
    }
  }, [location.search]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: null });
  };

  const submit = async (e) => {
    e.preventDefault();
    // client-side validation
    const nextErrors = {};
    if (!form.email) nextErrors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) nextErrors.email = "Invalid email format.";
    if (!form.otp) nextErrors.otp = "Token is required.";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      notify("error", { title: "Validation", text: "Please fix validation errors." });
      return;
    }
    if (loading) return;
    setLoading(true);
    try {
      // use apiFetch so X-Tenant header is set when present in path
      const res = await apiFetch(`/api/account/verify-email`, {
        skipTenant: true,
        method: "POST",
        body: JSON.stringify({ email: form.email, otp: form.otp }),
      });

      if (!res.ok) {
        let parsed = null;
        try {
          parsed = await res.json();
        } catch (err) {
          notify("error", { title: "Verify failed", text: res.statusText });
          setLoading(false);
          return;
        }

        const note = mapApiErrorToNotification(parsed, res.status);
        notify("error", note);
        setLoading(false);
        return;
      }

      const data = await res.json();
      if (data?.succeeded) {
        notify("success", { title: "Verified", text: data.message || "Email successfully verified." });
        // Prefer tenant-prefixed login route when tenant is known
        const tenant = localStorage.getItem("tenant");
        const tenantPrefix = (process.env.REACT_APP_TENANT_PREFIX || "t").trim();
        if (tenant) {
          navigate(`/${tenantPrefix}/${tenant}/login`);
        } else {
          navigate("/login");
        }
      } else {
        const note = mapApiErrorToNotification(data, res.status);
        notify("error", note);
      }
    } catch (err) {
      console.error(err);
      notify("error", { title: "Network error", text: "Could not reach server." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 h-screen items-center place-items-center">
        <div className="w-full max-w-md space-y-8 p-10 rounded-lg">
          <div>
            <img
              className="mx-auto h-20 w-auto"
              src={require("../assets/stokraze.png")}
              alt="Your Company"
            />
            <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
              Verify your email
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">Enter your email and the verification token you received.</p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={submit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email" className="sr-only">Email</label>
                <input id="email" name="email" type="email" autoComplete="email" required value={form.email} onChange={handleChange}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Email address" disabled={loading} />
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
              </div>
              <div className="mt-3">
                <label htmlFor="otp" className="sr-only">Token</label>
                <input id="otp" name="otp" type="text" required value={form.otp} onChange={handleChange}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Verification token" disabled={loading} />
                {errors.otp && <p className="mt-1 text-xs text-red-600">{errors.otp}</p>}
              </div>
            </div>

            <div>
              <button type="submit" disabled={loading} aria-busy={loading}
                className="group relative flex items-center justify-center w-full py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none">
                {loading && (
                  <svg className="animate-spin -ml-2 mr-3 h-12 w-12 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                )}
                {loading ? "Verifying..." : "Verify Email"}
              </button>
            </div>
          </form>
        </div>

        <div className="flex justify-center order-first sm:order-last">
          <img src={require("../assets/SignUp2.png")} alt="" />
        </div>
      </div>
    </>
  );
}
